// import React, { useEffect, useState, useRef, use } from 'react';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { candidatesAPI, resumesAPI, jobsAPI } from '../api/client';
import { DEPARTMENT_OPTIONS } from '../constants/departmentOptions';
import { toast } from 'react-toastify';
import { CANDIDATE_STATUSES, getStatusStyle, getStatusLabel } from '../constants/candidateStatuses';
import { hasPermission } from '../utils/accessControl';
import { mayPerform, mayPerformWithRole } from '../utils/userAccess';
import '../pages/AnimationsAndStyles.css';
import LoadingButton from '../components/LoadingButton';
import { TableSkeleton } from '../components/LoadingSkeleton';

const CANDIDATE_LIST_FILTERS_KEY = 'candidate_list_filters_v1';

const loadPersistedFilters = () => {
  try {
    const raw = sessionStorage.getItem(CANDIDATE_LIST_FILTERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/* ================= DATE FILTER HELPERS ================= */
// Parse a native <input type="date" /> value ('YYYY-MM-DD') as a LOCAL date.
// (new Date('YYYY-MM-DD') would be parsed as UTC and shift the day for some timezones.)
const parseDateInput = (value) => {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const startOfDay = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const endOfDay = (date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

const formatFilterDate = (value) => {
  const parsed = parseDateInput(value);
  return parsed
    ? parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';
};

const DATE_FILTER_MENU_LABELS = {
  today: 'Today',
  this_week: 'This Week',
  this_month: 'This Month',
  last_week: 'Last Week',
  last_month: 'Last Month',
  specific_date: 'Specific Date',
  custom: 'Custom Range',
};

export default function CandidatesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status');
  const showRequestedPendingTasks = searchParams.get('mine') === 'true';
  const initialFilters = showRequestedPendingTasks
    ? { statusFilter: requestedStatus || 'screen_pending' }
    : loadPersistedFilters();
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm || '');
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter || '');
  const [createdByFilter, setCreatedByFilter] = useState(initialFilters.createdByFilter || '');
  const [dateFilter, setDateFilter] = useState(initialFilters.dateFilter || '');
  const [specificDate, setSpecificDate] = useState(initialFilters.specificDate || '');
  const [customStartDate, setCustomStartDate] = useState(initialFilters.customStartDate || '');
  const [customEndDate, setCustomEndDate] = useState(initialFilters.customEndDate || '');
  const [departmentFilter, setDepartmentFilter] = useState(initialFilters.departmentFilter || '');
  const [mineOnly, setMineOnly] = useState(showRequestedPendingTasks);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCreatedByDropdown, setShowCreatedByDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const filterToolbarRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(Number(initialFilters.currentPage) > 0 ? Number(initialFilters.currentPage) : 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [resumeFile, setResumeFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [positionSearch, setPositionSearch] = useState('');
  const [showPositionSuggestions, setShowPositionSuggestions] = useState(false);
  const [creatorNames, setCreatorNames] = useState([]);
  
  // const [formData, setFormData] = useState({
  //   first_name: '',
  //   last_name: '',
  //   email_id: '',
  //   phone_number: '',
  //   alternate_mobile_number: '',
  //   skills: '',
  //   custom_overall_experience_years: '',
  //   education: '',
  //   position: '',
  //   offer_in_hand: '',
  //   variable_pay_details: '',
  //   rsu_details: ''
  // });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email_id: '',
    phone_number: '',
    alternate_mobile_number: '',
    skills: '',
    custom_overall_experience_years: '',
    education: '',
    position: '',
    job_id: '',
    offer_in_hand: '',
    variable_pay_details: '',
    rsu_details: '',
    source: '',
    joining_date: '',
    department: ''
  });

  
  // 🔥 NEW: Use ref to always have latest formData
  const formDataRef = useRef(formData);
  

const navigate = useNavigate();
// Get logged-in user from localStorage

const user = JSON.parse(localStorage.getItem("user"));
const canDeleteCandidate = mayPerformWithRole(user?.role, "candidate", "delete", "candidate.delete");
const canAddCandidate = mayPerformWithRole(user?.role, "candidate", "add", "candidate.create");

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const normalizeIndianPhone = (value) => {
  const digits = digitsOnly(value);

  if (digits.length === 12 && digits.startsWith('91')) {
    return digits.slice(2);
  }

  if (digits.length === 11 && digits.startsWith('0')) {
    return digits.slice(1);
  }

  return digits;
};

const isValidPhone = (value) => normalizeIndianPhone(value).length === 10;


  /* ================= 1. LOAD CANDIDATES ================= */
  useEffect(() => {
    fetchCandidates();
  }, []);

  // Keep ref in sync with state for fresh data in submit handler
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const fetchCandidates = async (p = 1) => {
    try {
      setIsLoadingCandidates(true);
      const params = { page: p, pageSize: 15 };
      if (searchTerm) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (mineOnly) params.mine = true;
      if (createdByFilter) params.created_by = createdByFilter;
      if (dateFilter) params.date = dateFilter;
      if (dateFilter === 'specific_date' && specificDate) {
        params.specificDate = specificDate;
      }
      if (dateFilter === 'custom') {
        const invalidRange =
          customStartDate && customEndDate && customStartDate > customEndDate; // ISO strings compare in order
        if (!invalidRange) {
          if (customStartDate) params.startDate = customStartDate;
          if (customEndDate) params.endDate = customEndDate;
        }
      }
      if (departmentFilter) params.department = departmentFilter;
      const res = await candidatesAPI.getAll(params);
      if (res.data?.pagination) {
        setCandidates(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalCount(res.data.pagination.total);
      } else {
        setCandidates(res.data);
        setTotalCount(res.data.length);
        const totalPages = Math.max(1, Math.ceil(res.data.length / 15));
        setTotalPages(totalPages);
      }
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
      toast.error('Failed to fetch candidates');
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadCreatorNames = async () => {
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        if (mineOnly) params.mine = true;
        const res = await candidatesAPI.getCreators(params);
        if (!isActive) return;

        const names = Array.isArray(res.data) ? res.data : [];
        setCreatorNames(names);
        setCreatedByFilter(current =>
          current && !names.includes(current) ? '' : current
        );
      } catch (err) {
        if (isActive) {
          console.error('Failed to fetch creator names:', err);
        }
      }
    };

    loadCreatorNames();

    return () => {
      isActive = false;
    };
  }, [statusFilter, mineOnly]);

  const fetchJobs = async () => {
    try {
      const res = await jobsAPI.getAll();
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    }
  };

  /* ================= 2. LIVE SEARCH FILTER ================= */
// Filtering is handled by the backend API, so we just use the candidates directly
// Apply only UI-level filters like status, department, etc. if needed
const filteredCandidates = candidates.filter(candidate => {
    // 1. STATUS
    if (statusFilter && candidate.status !== statusFilter) {
      return false;
    }

    // 2. CREATED BY
    if (createdByFilter && candidate.created_by_name !== createdByFilter) {
      return false;
    }

    // 3. DEPARTMENT
    if (departmentFilter && candidate.department !== departmentFilter) {
      return false;
    }

    // 4. DATE FILTER — a row matches when EITHER its created date or its
    // last-updated date falls in the selected window (mirrors the backend).
    if (dateFilter) {
      const matchesDate = (baseDate) => {
        if (!baseDate) return false;

        const date = new Date(baseDate);
        const now = new Date();

        if (dateFilter === 'today') {
          if (date.toDateString() !== now.toDateString()) return false;
          if (date > now) return false; // future dates should not show in "Today" filter
        }

        if (dateFilter === 'this_week') {
          const start = new Date(now);
          const day = now.getDay();
          const diff = day === 0 ? 6 : day - 1;

          start.setDate(now.getDate() - diff);
          start.setHours(0, 0, 0, 0);

          const end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);

          if (date < start || date > end) return false;
          if (date > now) return false; // future dates should not show in "This Week" filter
        }

        if (dateFilter === 'this_month') {
          if (
            date.getMonth() !== now.getMonth() ||
            date.getFullYear() !== now.getFullYear()
          ) return false;

          if (date > now) return false; // future dates should not show in "This Month" filter
        }

        if (dateFilter === 'last_week') {
          const startOfThisWeek = new Date(now);
          const day = now.getDay();
          const diff = day === 0 ? 6 : day - 1;
          startOfThisWeek.setDate(now.getDate() - diff);
          startOfThisWeek.setHours(0, 0, 0, 0);

          const start = new Date(startOfThisWeek);
          start.setDate(start.getDate() - 7);
          start.setHours(0, 0, 0, 0);

          const end = new Date(startOfThisWeek);
          end.setMilliseconds(-1);

          if (date < start || date > end) return false;
        }

        if (dateFilter === 'last_month') {
          const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
          const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

          if (date < start || date > end) return false;
        }

        if (dateFilter === 'specific_date') {
          const parsed = parseDateInput(specificDate);
          if (parsed) {
            const start = startOfDay(parsed);
            const end = endOfDay(parsed);

            if (date < start || date > end) return false;
          }
        }

        if (dateFilter === 'custom') {
          const parsedStart = parseDateInput(customStartDate);
          const parsedEnd = parseDateInput(customEndDate);

          const start = parsedStart ? startOfDay(parsedStart) : null;
          const end = parsedEnd ? endOfDay(parsedEnd) : null;

          if (start && end && start > end) return false; // invalid range
          if (start && date < start) return false;
          if (end && date > end) return false;
        }

        return true;
      };

      if (!matchesDate(candidate.created_at) && !matchesDate(candidate.updated_at)) {
        return false;
      }
    }

    return true;
  });

  const availableStatuses = CANDIDATE_STATUSES;
  const loggedInUserName = user?.name || user?.username || '';
  const availableCreatedByNames = mineOnly
    ? (loggedInUserName ? [loggedInUserName] : [])
    : creatorNames;
  const createdByFilterValue = mineOnly
    ? (createdByFilter || loggedInUserName || 'Logged-in user')
    : createdByFilter || 'All HR';
  const isCustomRangeInvalid =
    dateFilter === 'custom' && Boolean(customStartDate) && Boolean(customEndDate) && customStartDate > customEndDate;
  const dateFilterLabel =
    DATE_FILTER_MENU_LABELS[dateFilter] && dateFilter !== 'specific_date' && dateFilter !== 'custom'
      ? DATE_FILTER_MENU_LABELS[dateFilter]
      : dateFilter === 'specific_date'
        ? (specificDate ? formatFilterDate(specificDate) : 'Specific Date')
        : dateFilter === 'custom'
          ? (customStartDate && customEndDate
              ? `${formatFilterDate(customStartDate)} – ${formatFilterDate(customEndDate)}`
              : customStartDate
                ? `From ${formatFilterDate(customStartDate)}`
                : customEndDate
                  ? `Until ${formatFilterDate(customEndDate)}`
                  : 'Custom Range')
          : 'All Time';
  const hasActiveFilters = Boolean(searchTerm || statusFilter || createdByFilter || dateFilter || departmentFilter || mineOnly);
  const candidatesPerPage = 15;
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedCandidates = filteredCandidates.slice(0);
  const visiblePageNumbers = Array.from(
    { length: Math.min(4, totalPages) },
    (_, index) => {
      const startPage = Math.min(
        Math.max(1, safeCurrentPage - 1),
        Math.max(1, totalPages - 3)
      );
      return startPage + index;
    }
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchCandidates(1);
  }, [searchTerm, statusFilter, createdByFilter, dateFilter, specificDate, customStartDate, customEndDate, departmentFilter, mineOnly]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    sessionStorage.setItem(
      CANDIDATE_LIST_FILTERS_KEY,
      JSON.stringify({
        searchTerm,
        statusFilter,
        createdByFilter,
        dateFilter,
        specificDate,
        customStartDate,
        customEndDate,
        departmentFilter,
        currentPage,
      })
    );
  }, [searchTerm, statusFilter, createdByFilter, dateFilter, specificDate, customStartDate, customEndDate, departmentFilter, currentPage]);

  /* ================= 3. FORM INPUT HANDLER ================= */
  //const handleChange = (e) =>
   // setFormData({ ...prev, [e.target.name]: e.target.value }); 
// santhosh - this was causing issues with stale state when resume parsing was updating formData and user was also typing at the same time. So changed to functional update form to ensure we always have latest state.
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === 'phone_number' || name === 'alternate_mobile_number') {
      nextValue = normalizeIndianPhone(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: nextValue,
      ...(name === 'department' ? { position: '', job_id: '' } : {})
    }));
    if (name === 'department') {
      setPositionSearch('');
      setShowPositionSuggestions(false);
    }
  };

  // Job records can store either the parent department (ICDS / Embedded) or
  // one of its sub-departments (for example, ICDS - DV). Match both formats.
  const selectedDepartmentGroup = (formData.department || '').split(' - ')[0].trim().toLowerCase();
  const filteredJobs = selectedDepartmentGroup
    ? jobs.filter(job => {
        const jobDepartmentGroup = (job.department || '').split(' - ')[0].trim().toLowerCase();
        return jobDepartmentGroup === selectedDepartmentGroup;
      })
    : [];
  const normalizedPositionSearch = positionSearch.trim().toLocaleLowerCase();
  const visiblePositionJobs = filteredJobs.filter(job => {
    const searchableText = [job.title, job.client_name, job.department]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase();
    return !normalizedPositionSearch || searchableText.includes(normalizedPositionSearch);
  });

  console.log("Stored user:", user);
console.log("Role:", user?.role);
console.log("Can delete candidate:", canDeleteCandidate);


  /* ================= 4. SEARCH HANDLER ================= */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCreatedByFilter('');
    setDateFilter('');
    setSpecificDate('');
    setCustomStartDate('');
    setCustomEndDate('');
    setDepartmentFilter('');
    setMineOnly(false);
    setCurrentPage(1);
    setShowStatusDropdown(false);
    setShowCreatedByDropdown(false);
    setShowDateDropdown(false);
    setShowDepartmentDropdown(false);
    setSearchParams({}, { replace: true });
    sessionStorage.removeItem(CANDIDATE_LIST_FILTERS_KEY);
  };

  // Clicking anywhere outside the filter toolbar closes every open filter
  // dropdown (native <select> filters already close on outside click).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterToolbarRef.current && !filterToolbarRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
        setShowCreatedByDropdown(false);
        setShowDepartmentDropdown(false);
        setShowDateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ================= 5. RESUME AI PARSING ================ */
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file.type)) {
      toast.error('Only PDF and word files are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
   
    setResumeFile(file);
    setParsing(true);

    try {
      const fd = new FormData();
      fd.append("resume", file);

      const res = await resumesAPI.parseResume(fd);
      
      setFormData(prev => ({
        ...prev,
        // Only update EMPTY fields - don't overwrite if user already filled it
        first_name: prev.first_name || (res.data.first_name || ""),
        last_name: prev.last_name || (res.data.last_name || ""),
        email_id: prev.email_id || (res.data.email_id || ""),  
        phone_number: prev.phone_number || normalizeIndianPhone(res.data.phone_number),
        skills: prev.skills || (Array.isArray(res.data.skills)
          ? res.data.skills.join(", ")
          : res.data.skills || ""),
        custom_overall_experience_years: prev.custom_overall_experience_years || (res.data.experience || ""),
        education: prev.education || (res.data.education || ""),
      }));



      toast.success("Resume parsed successfully. Please review the details and click 'Save Candidate & Resume' to continue.");

    } catch (err) {
      console.error("Resume parsing failed:", err);
      toast.error("Resume parsing failed. Please fill the details manually.");
      // Keep file for manual upload
    } finally {
      setParsing(false);
    }
  };

  /* ================= 6. SAVE CANDIDATE + RESUME ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Use ref to get latest formData (avoids stale closure issues)
    const latestFormData = formDataRef.current;
    const dataToSave = {
      ...latestFormData,
      phone_number: normalizeIndianPhone(latestFormData.phone_number),
      alternate_mobile_number: latestFormData.alternate_mobile_number
        ? normalizeIndianPhone(latestFormData.alternate_mobile_number)
        : '',
    };

    if (!dataToSave.first_name || !String(dataToSave.first_name).trim()) {
      toast.error("Candidate first name is mandatory");
      return;
    }

    if (!dataToSave.department || !String(dataToSave.department).trim()) {
      toast.error("Department is mandatory");
      return;
    }

    if (!dataToSave.email_id || !dataToSave.phone_number) {
      toast.error("Email and Mobile number are mandatory");
      return;
    }

    if (!isValidEmail(dataToSave.email_id)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!isValidPhone(dataToSave.phone_number)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (
      dataToSave.alternate_mobile_number &&
      !isValidPhone(dataToSave.alternate_mobile_number)
    ) {
      toast.error("Please enter a valid 10-digit alternate mobile number");
      return;
    }

    try {
      setIsSubmitting(true);
      // Create candidate with fresh data
      const createRes = await candidatesAPI.create(dataToSave);
      const candidateId = createRes.data.id;

      // STEP 2: Upload resume if exists
      if (resumeFile) {
        const fd = new FormData();
        fd.append("resume", resumeFile);
        fd.append("candidate_id", candidateId);
        await resumesAPI.upload(fd);
        toast.success("Candidate saved successfully with resume");
      } else {
        toast.success("Candidate created successfully");
      }

      // Reset form
      setShowForm(false);
      setResumeFile(null);
      // setFormData({
      //   first_name: '',
      //   last_name: '',
      //   email_id: '',
      //   phone_number: '',
      //   alternate_mobile_number: '',
      //   skills: '',
      //   custom_overall_experience_years: '',
      //   education: '',
      //   position: '',
      //   offer_in_hand: '',
      //   variable_pay_details: '',
      //   rsu_details: '',
      // });
      setFormData({
        first_name: '',
        last_name: '',
        email_id: '',
        phone_number: '',
        alternate_mobile_number: '',
        skills: '',
        custom_overall_experience_years: '',
        education: '',
        position: '',
        job_id: '',
        offer_in_hand: '',
        variable_pay_details: '',
        rsu_details: '',
        source: '',
        joining_date: '',
        department: '',
      });

      fetchCandidates(); // Refresh list

    } catch (err) {
      console.error("Failed to add candidate:", err);
      if (err.response?.status === 409) {
        // Show the actual error message from backend (email or phone duplicate)
        toast.error(err.response?.data?.error || 'Candidate already exists');
      } else {
        toast.error('Failed to save candidate');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ================= 7. DELETE CANDIDATE ================= */
  const openDeleteModal = (candidateId) => {
    setCandidateToDelete(candidateId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setCandidateToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!candidateToDelete) return;
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      await candidatesAPI.delete(candidateToDelete);
      toast.success('Candidate deleted successfully');
      closeDeleteModal();
      fetchCandidates();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete candidate');
    } finally {
      setIsDeleting(false);
    }
  };

  /* ================= 8. MAIN RENDER ================= */
  return (
    <div style={styles.page}>
      <style>{css}</style>

      {showDeleteModal && (
        <div className="delete-modal-backdrop" onClick={closeDeleteModal}>
          <div
            className="delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            aria-describedby="delete-modal-description"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="delete-modal-close"
              onClick={closeDeleteModal}
              aria-label="Close delete warning"
            >
              ×
            </button>

            <div className="delete-modal-icon" aria-hidden="true">
              <span className="delete-modal-icon-line delete-modal-icon-line-1" />
              <span className="delete-modal-icon-line delete-modal-icon-line-2" />
            </div>

            <h2 id="delete-modal-title" className="delete-modal-title">
              Are you sure?
            </h2>
            <p id="delete-modal-description" className="delete-modal-description">
              Do you really want to delete this candidate record? This process cannot be undone.
            </p>

            <div className="delete-modal-actions">
              <button type="button" className="delete-modal-btn cancel" onClick={closeDeleteModal} disabled={isDeleting}>
                Cancel
              </button>
              <LoadingButton type="button" className="delete-modal-btn delete" onClick={handleDelete} disabled={isDeleting} loading={isDeleting} loadingText="Deleting...">
                Delete
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
          <div className="header">
        <div>
          <h2>Candidate Management</h2>
          <p className="subtitle">Manage and track all candidates ({totalCount})</p>
        </div>
        <button 
          className="cl-primary-btn" 
          disabled={!canAddCandidate}
          onClick={() => {
            setShowForm(!showForm);
            // Reset form when closing
            if (showForm) {
              setResumeFile(null);
              // setFormData({
              //   first_name: '',
              //   last_name: '',
              //   email_id: '',
              //   phone_number: '',
              //   skills: '',
              //   custom_overall_experience_years: '',
              //   alternate_mobile_number: '',
              //   education: '',
              //   position: ''
              // });
              setFormData({
                first_name: '',
                last_name: '',
                email_id: '',
                phone_number: '',
                skills: '',
                custom_overall_experience_years: '',
                alternate_mobile_number: '',
                education: '',
                position: '',
                joining_date: ''
              });
            }
          }}
        >
          {showForm ? 'Cancel' : 'Add Candidate'}
        </button>
      </div>

      {/* ADD CANDIDATE FORM */}
      {showForm && (
        <form className="card" onSubmit={handleSubmit}>
          <h3>📝 Add New Candidate</h3>

          {/* RESUME UPLOAD */}
          <label>Upload Resume (PDF or Word) - Auto Parse</label>
          <input 
            type="file" 
            accept=".pdf,.doc,.docx" 
            onChange={handleResumeUpload}
            disabled={parsing}
          />
          {parsing && (
            <div style={{
              marginTop: '-8px',
              marginBottom: '16px',
              color: '#3B82F6',
              fontSize: '13px',
              fontFamily: "var(--font-sans)"
            }}>
              Parsing resume...
            </div>
          )}
          {resumeFile && !parsing && (
            <div style={{
              marginTop: '-8px',
              marginBottom: '16px',
              color: '#10B981',
              fontSize: '13px',
              fontFamily: "var(--font-sans)"
            }}>
              Selected: {resumeFile.name}
            </div>
          )}

          {/* FORM FIELDS */}
          <div className="form-grid">
                <input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="First Name *"
                  required
                />

                <input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Last Name"
                />

                <input
                  name="email_id"
                  type="email"
                  value={formData.email_id}
                  onChange={handleChange}
                  placeholder="Email Address *"
                  required
                />

            <input 
              name="phone_number" 
              type="tel"
              inputMode="numeric"
              title="Enter a valid 10-digit mobile number"
              placeholder="Phone Number *" 
              value={formData.phone_number} 
              onChange={handleChange}
              required
            />
            <input 
              name="alternate_mobile_number" 
              type="tel"
              inputMode="numeric"
              title="Enter a valid 10-digit alternate mobile number"
              placeholder="Alternate Mobile Number" 
              value={formData.alternate_mobile_number} 
              onChange={handleChange}
            />
            <input 
              name="skills" 
              placeholder="Skills (React, Node.js, Python)" 
              value={formData.skills} 
              onChange={handleChange}
            />
            <input 
              name="custom_overall_experience_years" 
              placeholder="Experience (5 years)" 
              value={formData.custom_overall_experience_years} 
              onChange={handleChange}
            />
            <input 
              name="education" 
              placeholder="B.Tech Computer Science" 
              value={formData.education} 
              onChange={handleChange}
            />
            <input
              name="joining_date"
              type="date"
              value={formData.joining_date || ''}
              onChange={handleChange}
              title="Candidate Joining Date"
            />
            <select name="department" value={formData.department} onChange={handleChange} required>
              <option value="">Select Department *</option>
              {DEPARTMENT_OPTIONS.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select name="source" value={formData.source} onChange={handleChange}>
              <option value="">Select Source</option>
              <option value="Naukri">Naukri</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Employee Reference">Employee Reference</option>
              <option value="Candidate Reference">Candidate Reference</option>
            </select>
          </div>

          {/* POSITION DROPDOWN */}
          <label>Position</label>
          <div className="position-search-wrap">
            <button
              type="button"
              className="position-select-trigger"
              aria-haspopup="listbox"
              aria-expanded={showPositionSuggestions}
              disabled={!formData.department}
              onClick={() => {
                setShowPositionSuggestions(open => !open);
                setPositionSearch('');
              }}
            >
              <span>{formData.position || 'Select Position'}</span>
              <span aria-hidden="true">v</span>
            </button>
            {showPositionSuggestions && (
              <div className="position-suggestions">
                <input
                  type="search"
                  className="position-search-input"
                  aria-label="Search positions"
                  placeholder="Search by position or client..."
                  value={positionSearch}
                  onChange={e => setPositionSearch(e.target.value)}
                  autoFocus
                />
                <div className="position-suggestion-list" role="listbox" aria-label="Matching positions">
                  {visiblePositionJobs.length ? visiblePositionJobs.map(job => {
                    const positionLabel = `${job.title} - ${job.client_name}`;
                    return (
                      <button
                        type="button"
                        className="position-suggestion"
                        key={job.id}
                        role="option"
                        aria-selected={formData.position === positionLabel}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, position: positionLabel, job_id: job.id }));
                          setPositionSearch('');
                          setShowPositionSuggestions(false);
                        }}
                      >
                        {positionLabel}
                      </button>
                    );
                  }) : (
                    <div className="position-no-results">
                      {positionSearch.trim() ? 'No matching positions' : 'No positions available'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <LoadingButton className="cl-primary-btn" type="submit" disabled={parsing || isSubmitting} loading={isSubmitting} loadingText="Saving..." style={{marginTop: '20px'}}>
            Save Candidate &amp; Resume
          </LoadingButton>
        </form>
      )}

      {/* CANDIDATES TABLE */}
      <div className="card">
        <div className="search-header">
          <h3>All Candidates ({totalCount})</h3>
          {isLoadingCandidates ? <TableSkeleton rows={8} cols={7} /> : null}
          
          {/* SEARCH BAR */}
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, skill, email or phone..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
                {searchTerm && (
                  <button 
                    className="clear-search-btn" 
                    onClick={clearSearch}
                    type="button"
                  >
                    Clear
                  </button>
                )}
            </div>
          </div>
        </div>

        <div className="filter-toolbar" ref={filterToolbarRef}>
          <div className="filter-toolbar-left">
            <div className="filter-dropdown">
              <button
                type="button"
                className={`filter-trigger ${statusFilter ? 'active' : ''}`}
                onClick={() => {
                  setShowStatusDropdown(!showStatusDropdown);
                  setShowCreatedByDropdown(false);
                  setShowDateDropdown(false);
                }}
              >
                <span>Status</span>
                <span className="filter-value">{statusFilter ? getStatusLabel(statusFilter) : 'All Status'}</span>
                <span className="filter-arrow">↓</span>
              </button>

              {showStatusDropdown && (
                <div className="filter-menu">
                  <button
                    className="filter-option"
                    onClick={() => {
                      setStatusFilter('');
                      setShowStatusDropdown(false);
                    }}
                  >
                    All Status
                  </button>

                  {availableStatuses.map(status => (
                    <button
                      key={status.key}
                      className="filter-option"
                      onClick={() => {
                        setStatusFilter(status.key);
                        setShowStatusDropdown(false);
                      }}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button
                type="button"
                className={`filter-trigger ${createdByFilter || mineOnly ? 'active' : ''}`}
                onClick={() => {
                  setShowCreatedByDropdown(!showCreatedByDropdown);
                  setShowStatusDropdown(false);
                  setShowDateDropdown(false);
                }}
              >
                <span>Created By</span>
                <span className="filter-value">{createdByFilterValue}</span>
                <span className="filter-arrow">↓</span>
              </button>

              {showCreatedByDropdown && (
                <div className="filter-menu">
                  {!mineOnly && (
                    <button
                      type="button"
                      className={`filter-option ${!createdByFilter ? 'selected' : ''}`}
                      onClick={() => {
                        setCreatedByFilter('');
                        setShowCreatedByDropdown(false);
                      }}
                    >
                      All HR
                    </button>
                  )}
                  {availableCreatedByNames.map(name => (
                    <button
                      key={name}
                      type="button"
                      className={`filter-option ${createdByFilter === name ? 'selected' : ''}`}
                      onClick={() => {
                        setCreatedByFilter(name);
                        setShowCreatedByDropdown(false);
                      }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button
                type="button"
                className={`filter-trigger ${departmentFilter ? 'active' : ''}`}
                onClick={() => {
                  setShowDepartmentDropdown(!showDepartmentDropdown);
                  setShowStatusDropdown(false);
                  setShowCreatedByDropdown(false);
                  setShowDateDropdown(false);
                }}
              >
                <span>Department</span>
                <span className="filter-value">{departmentFilter || 'All Departments'}</span>
                <span className="filter-arrow">↓</span>
              </button>

              {showDepartmentDropdown && (
                <div className="filter-menu">
                  <button
                    className="filter-option"
                    onClick={() => {
                      setDepartmentFilter('');
                      setShowDepartmentDropdown(false);
                    }}
                  >
                    All Departments
                  </button>
                  {DEPARTMENT_OPTIONS.map(dept => (
                    <button
                      key={dept}
                      className={`filter-option ${departmentFilter === dept ? 'selected' : ''}`}
                      onClick={() => {
                        setDepartmentFilter(dept);
                        setShowDepartmentDropdown(false);
                      }}
                    >
                      {dept}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button
                type="button"
                className={`filter-trigger ${dateFilter ? 'active' : ''}`}
                onClick={() => {
                  setShowDateDropdown(!showDateDropdown);
                  setShowStatusDropdown(false);
                  setShowCreatedByDropdown(false);
                  setShowDepartmentDropdown(false);
                }}
              >
                <span>Date</span>
                <span className="filter-value">
                  {dateFilterLabel}
                </span>
                <span className="filter-arrow">↓</span>
              </button>
              
              {showDateDropdown && (
                <div className="filter-menu filter-menu-wide">
                  <button
                    className="filter-option"
                    onClick={() => {
                      setDateFilter('');
                      setShowDateDropdown(false);
                    }}
                  >
                    All Time
                  </button>

                  <button
                    className="filter-option"
                    onClick={() => {
                      setDateFilter('today');
                      setShowDateDropdown(false);
                    }}
                  >
                    Today
                  </button>

                  <button
                    className="filter-option"
                    onClick={() => {
                      setDateFilter('this_week');
                      setShowDateDropdown(false);
                    }}
                  >
                    This Week
                  </button>

                  <button
                    className="filter-option"
                    onClick={() => {
                      setDateFilter('this_month');
                      setShowDateDropdown(false);
                    }}
                  >
                    This Month
                  </button>

                  <button
                    className="filter-option"
                    onClick={() => {
                      setDateFilter('last_week');
                      setShowDateDropdown(false);
                    }}
                  >
                    Last Week
                  </button>

                  <button
                    className="filter-option"
                    onClick={() => {
                      setDateFilter('last_month');
                      setShowDateDropdown(false);
                    }}
                  >
                    Last Month
                  </button>

                  <button
                    className={`filter-option ${dateFilter === 'specific_date' ? 'selected' : ''}`}
                    onClick={() => {
                      setDateFilter('specific_date');
                      setShowDateDropdown(false);
                    }}
                  >
                    Specific Date
                  </button>

                  <button
                    className={`filter-option ${dateFilter === 'custom' ? 'selected' : ''}`}
                    onClick={() => {
                      setDateFilter('custom');
                      setShowDateDropdown(false);
                    }}
                  >
                    Custom Range
                  </button>
                </div>
              )}
            </div>

            {dateFilter === 'specific_date' && (
              <div className="filter-date-group">
                <label className="filter-date-label" htmlFor="candidate-specific-date">
                  Select Date
                </label>
                <div className="filter-date-single-row">
                  <input
                    id="candidate-specific-date"
                    type="date"
                    className="filter-date-input"
                    value={specificDate}
                    onChange={(e) => setSpecificDate(e.target.value)}
                  />
                  <div className="filter-date-actions">
                    <button
                      type="button"
                      className="btn outline"
                      onClick={() => setSpecificDate('')}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="cl-primary-btn"
                      onClick={() => setShowDateDropdown(false)}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            {dateFilter === 'custom' && (
              <div className="filter-date-group filter-date-range-pair">
                <div>
                  <label className="filter-date-label" htmlFor="candidate-date-from">
                    From
                  </label>
                  <input
                    id="candidate-date-from"
                    type="date"
                    className="filter-date-input"
                    value={customStartDate}
                    max={customEndDate || undefined}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="filter-date-label" htmlFor="candidate-date-to">
                    To
                  </label>
                  <input
                    id="candidate-date-to"
                    type="date"
                    className="filter-date-input"
                    value={customEndDate}
                    min={customStartDate || undefined}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>

                <div className="filter-date-actions">
                  <button
                    type="button"
                    className="btn outline"
                    onClick={() => {
                      setCustomStartDate('');
                      setCustomEndDate('');
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="cl-primary-btn"
                    onClick={() => setShowDateDropdown(false)}
                  >
                    Apply
                  </button>
                </div>

                {isCustomRangeInvalid && (
                  <div className="filter-date-error">
                    Start date cannot be after end date
                  </div>
                )}
              </div>
            )}
          </div>

          {hasActiveFilters && (
            <button type="button" className="btn outline filter-reset-btn" onClick={clearAllFilters}>
              Clear Filters
            </button>
          )}
        </div>

        {dateFilter && (
          <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '10px' }}>
            {statusFilter
              ? `Showing ${getStatusLabel(statusFilter)} candidates (${dateFilterLabel}) based on created or last updated date`
              : `Showing candidates (${dateFilterLabel}) based on created or last updated date`}
          </div>
        )}

        {/* EMPTY STATE */}
        {filteredCandidates.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-icon">
              {hasActiveFilters ? 'No Match' : 'Candidates'}
            </p>
            <p className="empty-state-text">
              {hasActiveFilters
                ? 'No candidates found for the selected filters. Try changing the filters or return to all candidates.'
                : 'No candidates found. Add your first candidate!'
              }
            </p>
            {hasActiveFilters && (
              <button className="btn outline" onClick={clearAllFilters} style={{marginTop: '16px'}}>
                View All Candidates
              </button>
            )}
          </div>
        ) : (
          /* TABLE */
          <>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th className="th-candidate">Candidate</th>
                  <th className="th-email">Email</th>
                  <th className="th-phone">Phone</th>
                  <th className="th-status">Status</th>
                  <th className="th-created">Created By</th>
                  <th className="th-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCandidates.map((c, idx) => (
                  <tr key={c.id} style={{animationDelay: `${idx * 0.05}s`}}>
                    <td className="name td-candidate">
                      <div className="avatar">
                        {(c.first_name || 'C')[0].toUpperCase()}
                      </div>
                      <div>
                        <strong>{c.first_name || 'Unknown'} {c.last_name || ''}</strong>
                        <div className="muted">ID #{c.id}</div>
                      </div>
                    </td>
                    <td className="td-email">{c.email_id || '-'}</td>
                    <td className="td-phone">{c.phone_number || '-'}</td>
                    <td className="td-status">
                      <span 
                        style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '600',
                          ...getStatusStyle(c.status)
                        }}
                      >
                        {getStatusLabel(c.status)}
                      </span>
                    </td>
                    <td className="td-created">{c.created_by_name || 'Unknown'}</td>
                    <td className="td-action">
                      <div className="action-buttons">
                        <button
                          className="act-btn"
                          onClick={() => navigate(`/candidates/${c.id}`)}
                        >
                          View
                        </button>

                        <button
                          className="act-btn act-edit"
                          onClick={() => navigate(`/feedback/${c.id}`)}
                        >
                          Feedback
                        </button>

                        {canDeleteCandidate && (
                          <button
                            className="act-btn act-delete"
                            onClick={() => openDeleteModal(c.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
            <div className="pagination-wrap">
              <button
                type="button"
                className="pagination-btn pagination-arrow"
                onClick={() => { const p = Math.max(1, currentPage - 1); setCurrentPage(p); fetchCandidates(p); }}
                disabled={safeCurrentPage === 1}
                aria-label="Previous page"
              >
                ←
              </button>

              {visiblePageNumbers.map(pageNumber => (
                <button
                  key={pageNumber}
                  type="button"
                  className={`pagination-btn ${safeCurrentPage === pageNumber ? 'active' : ''}`}
                  onClick={() => { setCurrentPage(pageNumber); fetchCandidates(pageNumber); }}
                >
                  {pageNumber}
                </button>
              ))}

              <button
                type="button"
                className="pagination-btn pagination-arrow"
                onClick={() => { const p = Math.min(totalPages, currentPage + 1); setCurrentPage(p); fetchCandidates(p); }}
                disabled={safeCurrentPage === totalPages}
                aria-label="Next page"
              >
                →
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}

/* ================= COMPLETE STYLES ================= */
const styles = {
  page: {
    padding: '4px 0',
    background: 'transparent',
    minHeight: 'auto',
    fontFamily: "var(--font-sans)",
    color: '#0F172A',
    animation: 'fadeInUp 0.6s ease-out',
  }
};

const css = `
/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
  flex-wrap: wrap;
}
.header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #0F172A;
  font-family: var(--font-sans);
}
.subtitle {
  color: #64748B;
  font-size: 13px;
  margin: 2px 0 0 0;
}

/* Cards */
.card {
  background: white;
  padding: 20px;
  border-radius: 16px;
  margin-bottom: 16px;
  box-shadow: var(--shadow-lg);
  animation: fadeInUp 0.5s ease-out;
}
.card:hover {
  box-shadow: var(--shadow-xl);
}

/* Search */
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 20px;
  flex-wrap: wrap;
}
.search-container {
  flex: 1;
  max-width: 360px;
  min-width: 220px;
}
.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.search-input {
  width: 100%;
  padding: 10px 80px 10px 14px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  font-size: 14px;
  font-family: var(--font-sans);
  background: #F8FAFC;
  transition: all 0.3s ease;
}
.search-input:focus {
  outline: none;
  border-color: #1E40AF;
  background: white;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}
.clear-search-btn {
  position: absolute;
  right: 12px;
  background: #E2E8F0;
  border: 1px solid #CBD5E1;
  border-radius: 999px;
  min-width: 54px;
  height: 26px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.2s ease;
}
.clear-search-btn:hover {
  background: #CBD5E1;
  color: #0F172A;
}

.filter-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.filter-toolbar-left {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: flex-end;
}
.filter-dropdown {
  position: relative;
}
.filter-trigger {
  min-width: 170px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #CBD5E1;
  border-radius: 10px;
  background: #F8FAFC;
  color: #0F172A;
  cursor: pointer;
  font-size: 13px;
  font-family: var(--font-sans);
  transition: all 0.2s ease;
}
.filter-trigger:hover {
  border-color: #94A3B8;
  background: #FFFFFF;
}
.filter-trigger.active {
  border-color: #1D4ED8;
  background: #EFF6FF;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}
.filter-value {
  flex: 1;
  text-align: left;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-arrow {
  color: #64748B;
  font-size: 12px;
}
.filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  max-height: 240px;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #CBD5E1;
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  z-index: var(--z-dropdown);
  padding: 6px 0;
}
.filter-option {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 8px 12px;
  font-size: 13px;
  font-family: var(--font-sans);
  color: #334155;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}
.filter-option:hover {
  background: #F1F5F9;
}
.filter-option.selected {
  background: #EFF6FF;
  color: #1D4ED8;
  font-weight: 700;
}
.filter-menu-wide {
  min-width: 260px;
  max-height: 360px;
}
/* Date range controls rendered in the filter row (Job List styling) */
.filter-date-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 1 auto;
}
.filter-date-label {
  display: block;
  margin: 0;
  font-size: 10px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.filter-date-input {
  width: 152px;
  height: 32px;
  padding: 6px 8px;
  margin-bottom: 0;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  background: #fff;
  color: #0F172A;
  font-size: 12px;
  font-family: var(--font-sans);
  box-sizing: border-box;
}
.filter-date-input:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 2px rgba(30, 64, 175, 0.1);
}
.filter-date-range-pair {
  flex-direction: row;
  gap: 4px;
  align-items: flex-end;
}
.filter-date-range-pair .filter-date-input {
  width: 138px;
}
.filter-date-single-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.filter-date-actions {
  display: flex;
  gap: 8px;
}
.filter-date-actions .btn {
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
  border-radius: 8px;
}
.filter-date-error {
  flex: 1 0 100%;
  color: #DC2626;
  font-size: 11px;
  margin: 2px 0 0 0;
}
.filter-reset-btn {
  white-space: nowrap;
}

/* Form */
label {
  display: block;
  margin: 12px 0 6px 0;
  font-weight: 600;
  color: #0F172A;
  font-size: 13px;
}
input, select {
  width: 100%;
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  font-size: 14px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  box-sizing: border-box;
}
input:focus, select:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}
.position-search-input {
  background: #fff;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  color: #334155;
}
.position-search-input::placeholder { color: #64748B; }
.position-search-wrap { position: relative; }
.position-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 42px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  text-align: left;
  font: 14px var(--font-sans);
  cursor: pointer;
}
.position-select-trigger:disabled { background: #F1F5F9; color: #94A3B8; cursor: not-allowed; }
.position-suggestions {
  position: relative;
  width: 100%;
  margin-top: -8px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #D7DFEA;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}
.position-suggestions .position-search-input { margin: 8px; width: calc(100% - 16px); }
.position-suggestion-list { max-height: 200px; overflow-y: auto; }
.position-suggestion {
  display: block;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  background: #fff;
  color: #334155;
  text-align: left;
  cursor: pointer;
  font: inherit;
}
.position-suggestion:hover, .position-suggestion[aria-selected="true"] { background: #F1F5F9; }
.position-no-results { padding: 10px 14px; color: #64748B; font-size: 14px; }
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 0;
}

/* Buttons */
.btn {
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-sans);
}
.btn.primary {
  background: linear-gradient(135deg, #1E40AF, #1e3a8a);
  color: white;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
}
.btn.primary:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(30, 64, 175, 0.4);
}
.btn.outline {
  background: transparent;
  color: #1E40AF;
  border: 2px solid #1E40AF;
  padding: 7px 14px;
  font-size: 12px;
}
.btn.outline:hover {
  background: #1E40AF;
  color: white;
}
.btn.danger {
  background: linear-gradient(135deg, #EF4444, #DC2626);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  padding: 7px 14px;
  font-size: 12px;
}
  .btn.danger:hover {
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
  }

  /* Delete Confirmation Modal */
  .delete-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.58);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 2000;
    animation: fadeInUp 0.18s ease-out;
  }

  .delete-modal {
    position: relative;
    width: 100%;
    max-width: 420px;
    background: #FFFFFF;
    border-radius: 18px;
    padding: 24px 20px 20px;
    box-shadow: var(--shadow-xl);
    text-align: center;
    animation: scaleIn 0.2s ease-out;
  }

  .delete-modal-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    border: none;
    background: transparent;
    color: #94A3B8;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  .delete-modal-close:hover {
    color: #475569;
    background: #F8FAFC;
  }

  .delete-modal-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 2px solid #EF4444;
    margin: 0 auto 16px;
    position: relative;
  }

  .delete-modal-icon-line {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 36px;
    height: 3px;
    background: #EF4444;
    border-radius: 999px;
    transform-origin: center;
  }

  .delete-modal-icon-line-1 {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .delete-modal-icon-line-2 {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  .delete-modal-title {
    margin: 0 0 8px;
    font-size: 20px;
    color: #0F172A;
    font-weight: 600;
    font-family: var(--font-sans);
  }

  .delete-modal-description {
    margin: 0 auto;
    max-width: 300px;
    font-size: 14px;
    line-height: 1.6;
    color: #64748B;
    font-family: var(--font-sans);
  }

  .delete-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-top: 20px;
  }

  .delete-modal-btn {
    min-width: 116px;
    padding: 11px 18px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    font-family: var(--font-sans);
  }

  .delete-modal-btn:hover {
    transform: translateY(-1px);
  }

  .delete-modal-btn.cancel {
    background: #D1D5DB;
    color: #374151;
  }

  .delete-modal-btn.cancel:hover {
    background: #BFC6D1;
  }

  .delete-modal-btn.delete {
    background: #EF4444;
    color: #FFFFFF;
    box-shadow: 0 8px 18px rgba(239, 68, 68, 0.24);
  }

  .delete-modal-btn.delete:hover {
    background: #DC2626;
    box-shadow: 0 10px 20px rgba(239, 68, 68, 0.30);
  }
  
  /* Table */
  .table-wrapper {
    overflow-x: auto;
  }
  .th-candidate { min-width: 160px; }
  .th-email     { min-width: 170px; }
  .th-phone     { min-width: 120px; white-space: nowrap; }
  .th-status    { min-width: 130px; }
  .th-created   { min-width: 100px; white-space: nowrap; }
  .th-action    { min-width: 190px; white-space: nowrap; text-align: right; }
  .td-action    { text-align: right; white-space: nowrap; vertical-align: middle; }
  .action-buttons {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    flex-wrap: nowrap;
    justify-content: flex-end;
  }
.pagination-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.pagination-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  color: #1E293B;
  font-size: 14px;
  font-family: var(--font-sans);
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all 0.2s ease;
}
.pagination-btn:hover:not(:disabled) {
  box-shadow: var(--shadow-md);
}
.pagination-btn.active {
  background: #0F4C81 !important;
  color: #fff !important;
  border-color: #0F4C81 !important;
  font-weight: 800 !important;
  text-decoration: underline !important;
  box-shadow: var(--shadow-lg);
}
.pagination-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: var(--shadow-sm);
}
.pagination-arrow {
  font-size: 24px;
  line-height: 1;
}
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
th {
  padding: 12px 10px;
  text-align: left;
  font-weight: 600;
  color: #0F172A;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #E2E8F0;
  background: linear-gradient(90deg, #F8FAFC, #F0F4FF);
}
td {
  padding: 10px;
  border-bottom: 1px solid #E2E8F0;
  color: #0F172A;
  font-size: 13px;
}
.name {
  display: flex;
  gap: 10px;
  align-items: center;
}
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1E40AF, #0EA5E9);
  color: white;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.status {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}
.status.applied { background: #EFF6FF; color: #1E40AF; }
.status.interview { background: #F0FDF4; color: #10B981; }
.status.offered { background: #DCFCE7; color: #166534; }
.status.rejected { background: #FEF2F2; color: #EF4444; }
.muted {
  color: #94A3B8;
  font-size: 12px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}
.empty-state-icon {
  font-size: 48px;
  margin-bottom: 8px;
}
.empty-state-text {
  color: #64748B;
  font-size: 14px;
  margin: 0;
}

/* Animations */
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  /* Responsive */
  @media (max-width: 768px) {
  .header, .search-header { flex-direction: column; align-items: stretch; }
  .filter-toolbar { align-items: stretch; }
  .filter-toolbar-left { flex-direction: column; }
  .filter-trigger { width: 100%; min-width: 0; }
  .filter-date-group, .filter-date-range-pair { width: 100%; }
  .filter-date-range-pair > div { flex: 1; }
  .filter-date-input, .filter-date-range-pair .filter-date-input { width: 100%; }
  .pagination-btn { width: 40px; height: 40px; font-size: 14px; }
  .pagination-arrow { font-size: 24px; }
  .form-grid { grid-template-columns: 1fr; }
  .name { flex-direction: column; align-items: flex-start; }
  th, td { padding: 12px 8px; }
  .delete-modal {
    max-width: 340px;
    padding: 30px 20px 22px;
  }
  .delete-modal-actions {
    flex-direction: column;
  }
  .delete-modal-btn {
    width: 100%;
  }
  }
  /* Action buttons — match Jobs page style */
  .act-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid #E2E8F0;
    background: #fff;
    color: #475569;
    transition: all 0.15s;
    font-family: 'Inter', sans-serif;
    white-space: nowrap;
    line-height: 1.4;
    letter-spacing: 0.2px;
  }
  .act-btn:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
    color: #1E293B;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  }
  .act-edit {
    color: #4A6FA5;
    border-color: rgba(74, 111, 165, 0.25);
  }
  .act-edit:hover {
    background: #4A6FA5;
    border-color: #4A6FA5;
    color: #fff;
  }
  .act-delete {
    color: #DC2626;
    border-color: rgba(220, 38, 38, 0.25);
  }
  .act-delete:hover {
    background: #DC2626;
    border-color: #DC2626;
    color: #fff;
  }
  .cl-primary-btn {
    padding: 7px 16px;
    background: #4A6FA5;
    color: #fff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    transition: background 0.15s;
    white-space: nowrap;
    font-family: 'Inter', sans-serif;
  }
  .cl-primary-btn:hover:not(:disabled) {
    background: #3A5A85;
  }
  .cl-primary-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;



